import { type ComponentType } from 'react'
import { useI18n } from '@muse/i18n'

import { type AnalyticsPlacementTypes, sendEvent } from '@core/analytics'
import { copyToClipboard } from '@core/clipboard'
import { getCollectionRssChannelUrl } from '@core/api'
import { DIALOG_IDS, type TDialogId, modalStore } from '@core/modal'
import { type TCollectionSimple } from '@core/models'
import { sessionDialogStore } from '@core/session-dialogs'
import { useActions, useValues } from '@core/store'
import { DownloadIcon, PencilIcon, RssIcon, ShareIcon, toast, TrashBinIcon } from '@ds/components'
import { setPlaybackContext } from '@features/audio-player'
import {
    AsyncDeleteCollectionDialogContainer,
    AsyncEditCollectionAudioDialogContainer,
    getCollectionPageUrl,
    type TCollectionDialogTarget,
    useCollectionDownload,
    useCollectionPlayback,
} from '@features/collection'
import { type TShareCollectionEntity, useShareDialog } from '@features/share'

import { CollectionActions, type TCollectionActionMenuItem } from '../components/collection-actions'

type TOwnDialogProps = {
    target: TCollectionDialogTarget
    onClose: () => void
}

// The dialogs this screen owns: their only trigger is this menu and their subject is the collection
// already in scope, so the screen mounts them. Opening still goes through `modalStore`, which keeps
// them exclusive with every other dialog and visible to whoever reads `activeDialog`.
const OWN_DIALOGS: Partial<Record<TDialogId, ComponentType<TOwnDialogProps>>> = {
    [DIALOG_IDS.deleteCollection]: AsyncDeleteCollectionDialogContainer,
    [DIALOG_IDS.editCollectionAudio]: AsyncEditCollectionAudioDialogContainer,
}

type TCollectionActionsContainerProps = {
    collection: TCollectionSimple
    placement: AnalyticsPlacementTypes
    isEditor: boolean
}

// Hero action bar — audio-web `CollectionActions`. Inline buttons: PLAY (primary), then Add/remove
// for the owner. "More" menu order matches nextjs exactly — visitor: share, rss, download; owner:
// share, edit, rss, download, delete. Edit hard-navs to the nextjs edit page (coexistence),
// delete/add-remove open the migrated dialogs. All gated by the same conditions as audio-web
// `useCollectionActions`.
export const CollectionActionsContainer = ({
    collection,
    placement,
    isEditor,
}: TCollectionActionsContainerProps) => {
    const { t } = useI18n()
    const playback = useCollectionPlayback(collection)
    const share = useShareDialog()
    const { download } = useCollectionDownload()
    const [activeDialog] = useValues([modalStore.props.getActiveDialog])
    const modalActions = useActions(modalStore.actions)
    const { setPendingNextjsExit } = useActions({
        setPendingNextjsExit: sessionDialogStore.actions.setPendingNextjsExit,
    })

    const canModify = isEditor && !collection.isImmutable
    const canPlay = collection.itemsCount > 0
    const canShare = !collection.isPrivate
    const canDownload = collection.itemsCount > 0 && collection.isDownloadable

    const handleEdit = () => {
        sendEvent({ name: 'collection_edit_click', payload: { collection: collection.id } })
        setPendingNextjsExit(true)
        window.location.assign(
            `${getCollectionPageUrl({
                authorUsername: collection.authorUsername,
                slug: collection.slug,
            })}/edit?from=${window.location.pathname}`,
        )
    }

    const handleDelete = () => {
        modalActions.open(DIALOG_IDS.deleteCollection)
    }

    const handleAddRemoveAudio = () => {
        modalActions.open(DIALOG_IDS.editCollectionAudio)
    }

    const handleDialogClose = () => {
        modalActions.close()
    }

    const handlePlay = () => {
        sendEvent({
            name: playback.isPlaying ? 'pause_button_click' : 'play_button_click',
            payload: {
                collection: collection.id,
                params__key: ['player_placement'],
                params__str_value: [placement],
            },
        })
        setPlaybackContext(playback.isPlaying ? 'pause' : 'play', placement)
        playback.playPause()
    }

    const handleShare = () => {
        const entity: TShareCollectionEntity = {
            item: 'collection',
            id: collection.id,
            title: collection.title,
            placement,
            authorUsername: collection.authorUsername,
            collectionSlug: collection.slug,
            itemsCount: collection.itemsCount,
        }
        sendEvent({
            name: 'share_button_click',
            payload: {
                collection: collection.id,
                params__key: ['player_placement'],
                params__str_value: [placement],
            },
        })
        share.open(entity)
    }

    const handleRss = () => {
        sendEvent({ name: 'collection_rss_click', payload: { collection: collection.id } })
        const url = `${window.location.origin}/${getCollectionRssChannelUrl(collection.id)}`
        copyToClipboard(url)
            .then(() => toast.success(t('i18n.commonRssCopied')))
            .catch(() => undefined)
    }

    const handleDownload = () => {
        download({ id: collection.id, title: collection.title }).catch(() => undefined)
    }

    // Fixed order matches audio-web `MORE_ACTIONS`: a visitor keeps only share/rss/download; the
    // owner-only edit and delete slot in between when `canModify`.
    const menuItems: TCollectionActionMenuItem[] = [
        ...(canShare
            ? [
                  {
                      key: 'share',
                      Icon: ShareIcon,
                      label: t('i18n.collectionShare'),
                      onSelect: handleShare,
                  },
              ]
            : []),
        ...(canModify
            ? [
                  {
                      key: 'edit',
                      Icon: PencilIcon,
                      label: t('i18n.commonEdit'),
                      onSelect: handleEdit,
                  },
              ]
            : []),
        { key: 'rss', Icon: RssIcon, label: t('i18n.collectionRss'), onSelect: handleRss },
        ...(canDownload
            ? [
                  {
                      key: 'download',
                      Icon: DownloadIcon,
                      label: t('i18n.collectionDownload'),
                      onSelect: handleDownload,
                  },
              ]
            : []),
        ...(canModify
            ? [
                  {
                      key: 'delete',
                      Icon: TrashBinIcon,
                      label: t('i18n.commonDelete'),
                      onSelect: handleDelete,
                  },
              ]
            : []),
    ]

    const OpenDialog = activeDialog ? OWN_DIALOGS[activeDialog] : undefined

    return (
        <>
            <CollectionActions
                canPlay={canPlay}
                isPlaying={playback.isPlaying}
                isPlayLoading={playback.isLoading}
                playLabel={playback.isPlaying ? t('i18n.collectionStop') : t('i18n.collectionPlay')}
                onPlay={handlePlay}
                canModify={canModify}
                addRemoveLabel={t('i18n.collectionAddRemoveAudio')}
                onAddRemove={handleAddRemoveAudio}
                moreLabel={t('i18n.collectionMoreActions')}
                menuItems={menuItems}
            />
            {OpenDialog && <OpenDialog target={collection} onClose={handleDialogClose} />}
        </>
    )
}
